import { LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import {
  AUDIT_OPERATION_VARIANT,
  AuditOperation,
} from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.constants';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';

/** The Forms service merges the company's FormOverride rows into the Form's history. */
export const FORM_AUDIT_ENTITY_TYPE = 'Form';

export const FORM_OVERRIDE_AUDIT_ENTITY_TYPE = 'FormOverride';

export const FORM_HISTORY_SHADOWED_TOGGLE = { label: 'Show what does not affect you' };

export const FormTemplateField = {
  TEMPLATE_JSON: 'TemplateJson',
  HTML_STORAGE_PATH: 'HtmlStoragePath',
  PDF_STORAGE_PATH: 'PdfStoragePath',
  PDF_METADATA: 'PdfMetadata',
  PDF_METADATA_PATH: 'PdfMetadataPath',
};

export const FORM_HISTORY_HIDDEN_FIELDS = [FormTemplateField.PDF_METADATA_PATH];

/**
 * A field whose changeset already covers its companion, written by the same save: a Prism save
 * writes the HTML the document renders to, and a PDF upload's block shows the field mapping too.
 */
export const TEMPLATE_COMPANION_FIELD = {
  [FormTemplateField.TEMPLATE_JSON]: FormTemplateField.HTML_STORAGE_PATH,
  [FormTemplateField.PDF_STORAGE_PATH]: FormTemplateField.PDF_METADATA,
};

// Large or storage-only values: the snapshot would show a blob path or a whole template document.
export const FORM_HISTORY_SNAPSHOT_EXCLUDED_FIELDS = [
  FormTemplateField.TEMPLATE_JSON,
  FormTemplateField.PDF_STORAGE_PATH,
  FormTemplateField.HTML_STORAGE_PATH,
  FormTemplateField.PDF_METADATA,
];

/** `change.summary.kind` of the template fields, as the Forms service summarises them. */
export const FormSummaryKind = {
  PRISM_TEMPLATE: 'forms.prismTemplate',
  PDF_FILE: 'forms.pdfFile',
  PDF_FIELD_MAPPING: 'forms.pdfFieldMapping',
  HTML_FILE: 'forms.htmlFile',
};

export const FormHistoryPanel = {
  TEMPLATE: 'template',
};

export const TEMPLATE_CHANGES_PANEL_TITLE = 'Template changes';

/** Changes on every save, so it goes last in the field panel, marked automatic. */
export const FORM_HISTORY_AUTOMATIC_FIELDS = ['Version'];

// Snapshot and Raw JSON show stored values as they are; they stay off until a claim gates them.
export const ARE_RAW_HISTORY_VIEWS_ENABLED = false;

export const TemplateChangeKind = {
  NONE: 'None',
  PRISM: 'Prism',
  PDF: 'Pdf',
  HTML: 'Html',
};

export const TemplateChangesStatus = {
  OK: 'Ok',
  TOO_LARGE: 'TooLarge',
  REIMPORTED: 'Reimported',
  UNAVAILABLE: 'Unavailable',
};

export const TemplateSide = {
  BEFORE: 'before',
  AFTER: 'after',
  CURRENT: 'current',
};

export const TEMPLATE_SIDES = [TemplateSide.BEFORE, TemplateSide.AFTER];

export const CLOSED_FILE_VIEW = { side: null, isOpen: false };

export const TemplatePartGroup = {
  DYNAMIC_FIELD: 'DynamicField',
  SIGNATURE: 'Signature',
  TEXT: 'Text',
  IMAGE: 'Image',
  LAYOUT: 'Layout',
};

/** Part groups in display order; `countKey` names the group in the host's per-side totals. */
export const TEMPLATE_PART_GROUPS = [
  { group: TemplatePartGroup.DYNAMIC_FIELD, title: 'Dynamic fields', countKey: 'dynamicFields' },
  { group: TemplatePartGroup.SIGNATURE, title: 'Signatures', countKey: 'signatures' },
  { group: TemplatePartGroup.TEXT, title: 'Text', countKey: 'text' },
  { group: TemplatePartGroup.IMAGE, title: 'Images', countKey: 'images' },
  { group: TemplatePartGroup.LAYOUT, title: 'Layout', countKey: 'layout' },
];

export const TemplatePartChange = {
  ADDED: 'Added',
  REMOVED: 'Removed',
  MOVED: 'Moved',
  REBOUND: 'Rebound',
  REFORMATTED: 'Reformatted',
  EDITED: 'Edited',
  REPLACED: 'Replaced',
};

export const PART_CHANGE_LABEL = {
  [TemplatePartChange.ADDED]: 'Added',
  [TemplatePartChange.REMOVED]: 'Removed',
  [TemplatePartChange.MOVED]: 'Moved',
  [TemplatePartChange.REBOUND]: 'Re-bound',
  [TemplatePartChange.REFORMATTED]: 'Reformatted',
  [TemplatePartChange.EDITED]: 'Edited',
  [TemplatePartChange.REPLACED]: 'Replaced',
};

export const PART_CHANGE_VARIANT = {
  [TemplatePartChange.ADDED]: AUDIT_OPERATION_VARIANT[AuditOperation.CREATED],
  [TemplatePartChange.REMOVED]: AUDIT_OPERATION_VARIANT[AuditOperation.DELETED],
  [TemplatePartChange.EDITED]: AUDIT_OPERATION_VARIANT[AuditOperation.UPDATED],
  [TemplatePartChange.MOVED]: LabelVariant.GRAY,
  [TemplatePartChange.REBOUND]: LabelVariant.PURPLE,
  [TemplatePartChange.REFORMATTED]: LabelVariant.ORANGE,
  [TemplatePartChange.REPLACED]: LabelVariant.CYAN,
};

/** How a part's row reads: one row per part, however many ways the part changed. */
export const PartRowKind = {
  ADDED: 'added',
  REMOVED: 'removed',
  REPLACED: 'replaced',
  CHANGED: 'changed',
  MOVED: 'moved',
};

/** Row kinds in precedence and summary order: a part both moved and edited reads as changed. */
export const PART_ROW_KINDS = [
  PartRowKind.ADDED,
  PartRowKind.REMOVED,
  PartRowKind.REPLACED,
  PartRowKind.CHANGED,
  PartRowKind.MOVED,
];

export const PART_ROW_KIND_BY_CHANGE = {
  [TemplatePartChange.ADDED]: PartRowKind.ADDED,
  [TemplatePartChange.REMOVED]: PartRowKind.REMOVED,
  [TemplatePartChange.REPLACED]: PartRowKind.REPLACED,
  [TemplatePartChange.EDITED]: PartRowKind.CHANGED,
  [TemplatePartChange.REFORMATTED]: PartRowKind.CHANGED,
  [TemplatePartChange.REBOUND]: PartRowKind.CHANGED,
  [TemplatePartChange.MOVED]: PartRowKind.MOVED,
};

export const PART_ROW_KIND_LABEL = {
  [PartRowKind.ADDED]: 'Added',
  [PartRowKind.REMOVED]: 'Removed',
  [PartRowKind.REPLACED]: 'Replaced',
  [PartRowKind.CHANGED]: 'Changed',
  [PartRowKind.MOVED]: 'Moved',
};

// The word carries the kind; colour only backs it up, so it reads the same without it.
export const PART_ROW_KIND_COLOR = {
  [PartRowKind.ADDED]: 'success.main',
  [PartRowKind.REMOVED]: 'error.main',
  [PartRowKind.REPLACED]: 'text.secondary',
  [PartRowKind.CHANGED]: 'text.secondary',
  [PartRowKind.MOVED]: 'text.secondary',
};

export const PART_GROUP_ARROW_SIZE = 16;

export const IMAGE_PART_NAME = 'Image';

export const TABLE_NODE_TYPE = 'table';

export const IN_TABLE_NOTE = 'In a table';

export const FORMATTING_CHANGED_NOTE = 'Formatting changed';

/** A re-bound field whose earlier label was not recorded. */
export const REBOUND_NOTE = 'Reads a different field';

/** How much of the enclosing block's text the row's location quotes. */
export const CONTAINER_PREVIEW_LENGTH = 48;

export const TemplateRegion = {
  BODY: 'Body',
  HEADER: 'Header',
  FOOTER: 'Footer',
};

export const REGION_LABEL = {
  [TemplateRegion.BODY]: 'Body',
  [TemplateRegion.HEADER]: 'Header',
  [TemplateRegion.FOOTER]: 'Footer',
};

export const FieldMappingColumn = {
  FIELD: 'field',
  CHANGE: 'change',
  POSITION: 'position',
};

export const FIELD_MAPPING_COLUMN_LABELS = {
  [FieldMappingColumn.FIELD]: 'Field',
  [FieldMappingColumn.CHANGE]: 'Change',
};

const PdfFieldKind = {
  FIELD: 'Field',
  SIGNATURE: 'Signature',
  FIGURE: 'Figure',
};

export const PDF_FIELD_KIND_LABEL = {
  [PdfFieldKind.FIELD]: 'Field',
  [PdfFieldKind.SIGNATURE]: 'Signature',
  [PdfFieldKind.FIGURE]: 'Figure',
};

export const SHORT_CHECKSUM_LENGTH = 12;

export const POSITION_DECIMALS = 0;

export const TEMPLATE_LOADING_LABEL = 'Loading template changes…';

export const TEMPLATE_LOAD_FAILED_MESSAGE = 'Could not load the template changes.';

const TEMPLATE_TOO_LARGE_MESSAGE = 'Template too big to compare.';

const TEMPLATE_REIMPORTED_MESSAGE =
  "This version was re-imported from HTML, so parts can't be matched.";

const TEMPLATE_UNREADABLE_MESSAGE = "The stored template can't be read as a Prism document.";

export const NO_PART_CHANGES_MESSAGE = 'No template parts changed; only the saved version did.';

export const PRISM_STATUS_MESSAGE = {
  [TemplateChangesStatus.TOO_LARGE]: TEMPLATE_TOO_LARGE_MESSAGE,
  [TemplateChangesStatus.REIMPORTED]: TEMPLATE_REIMPORTED_MESSAGE,
  [TemplateChangesStatus.UNAVAILABLE]: TEMPLATE_UNREADABLE_MESSAGE,
};

export const ANOTHER_BLOCK_NOTE = 'in another block';

export const NO_PARTS_LABEL = 'no parts';

export const FILE_FACTS_UNAVAILABLE_MESSAGE = "File details aren't available for this change.";

export const NO_TEMPLATE_LABEL = 'No template';

export const NO_FILE_LABEL = 'No file';

export const FILE_NOT_AVAILABLE_LABEL = 'File no longer available';

export const EMPTY_PARAGRAPH_LABEL = '(empty paragraph)';

export const FIELD_MAPPING_TITLE = 'Field mapping changes';

export const HTML_FIELDS_MARKED_NOTE =
  'Dynamic fields and signatures show their names, highlighted: this is the stored template, not a filled form.';

const HTML_TEMPLATE_FIELD_TYPE_ATTRIBUTE = 'data-template-field-type';

/** The HTML editor's field widgets: each names its field by attribute and keeps an empty slot. */
export const HtmlTemplateFieldType = {
  DYNAMIC_FIELD: 'dynamicField',
  SIGNATURE: 'helloSignField',
};

export const HTML_TEMPLATE_FIELD_SELECTOR = Object.values(HtmlTemplateFieldType)
  .map(type => `[${HTML_TEMPLATE_FIELD_TYPE_ATTRIBUTE}="${type}"]`)
  .join(', ');

export const HtmlTemplateFieldAttribute = {
  TYPE: HTML_TEMPLATE_FIELD_TYPE_ATTRIBUTE,
  DYNAMIC_FIELD_ID: 'data-dynamic-value',
  SIGNER: 'data-hellosign-signer',
};

export const HTML_TEMPLATE_FIELD_SLOT_SELECTOR = '.content';

export const UNKNOWN_DYNAMIC_FIELD_LABEL = 'Unknown field';

export const SIGNATURE_FIELD_LABEL = 'Signature';

export const SAME_FILE_NOTE = "The file didn't change.";

export const FIELD_MAPPING_UNREADABLE_MESSAGE = "The field mapping can't be read.";

export const HtmlChange = {
  ADDED: 'added',
  REMOVED: 'removed',
  REPLACED: 'replaced',
  UNCHANGED: 'unchanged',
};

export const HTML_CHANGE_TITLE = {
  [HtmlChange.ADDED]: 'HTML template added',
  [HtmlChange.REMOVED]: 'HTML template removed',
  [HtmlChange.REPLACED]: 'HTML template replaced',
  [HtmlChange.UNCHANGED]: "The HTML template didn't change.",
};

export const FIELD_MAPPING_CHANGE_COLUMN_WIDTH = '120px';

const FILE_SIDE_LABEL = {
  [TemplateSide.BEFORE]: 'Previous',
  [TemplateSide.AFTER]: 'This version',
};

export const OPEN_FILE_LABEL = {
  [TemplateSide.BEFORE]: 'Open previous',
  [TemplateSide.AFTER]: 'Open this version',
};

export const FILE_MODAL_TITLE = {
  [TemplateSide.BEFORE]: 'Previous version',
  [TemplateSide.AFTER]: 'This version',
};

export const FileFactLabel = {
  SIZE: 'Size',
  PAGES: 'Pages',
  UPLOADED: 'Uploaded',
  SAVED: 'Saved',
  CHECKSUM: 'SHA-256',
};

const COPY_CHECKSUM_LABEL = {
  [TemplateSide.BEFORE]: 'Copy previous SHA-256',
  [TemplateSide.AFTER]: "Copy this version's SHA-256",
};

/** How a change's two template sides read: the version before it, and the version it saved. */
export const CHANGE_SIDE_LABELS = {
  title: FILE_SIDE_LABEL,
  copyChecksum: COPY_CHECKSUM_LABEL,
  position: 'Page before → after',
};

/** How a compare's two template sides read: the listed version, and the form as it is now. */
export const COMPARE_SIDE_LABELS = {
  title: {
    [TemplateSide.BEFORE]: 'This version',
    [TemplateSide.AFTER]: 'Current',
  },
  copyChecksum: {
    [TemplateSide.BEFORE]: "Copy this version's SHA-256",
    [TemplateSide.AFTER]: 'Copy the current SHA-256',
  },
  position: 'Page in this version → current',
};

export const CHECKSUM_COPIED_MESSAGE = 'SHA-256 copied to clipboard';

export const RAW_JSON_TAB = { id: 'raw-json', label: 'Raw JSON' };

export const TEMPLATE_SIDE_LABEL = {
  [TemplateSide.BEFORE]: 'Before',
  [TemplateSide.AFTER]: 'After',
};

export const COPY_RAW_JSON_LABEL = {
  [TemplateSide.BEFORE]: 'Copy before JSON',
  [TemplateSide.AFTER]: 'Copy after JSON',
};

export const RAW_JSON_COPIED_MESSAGE = 'Template JSON copied to clipboard';

export const RAW_JSON_LOADING_LABEL = 'Loading template JSON…';

export const RAW_JSON_LOAD_FAILED_MESSAGE = 'Could not load the template JSON.';

export const HTML_FRAME_TITLE = 'HTML template';

export const FILE_LOADING_LABEL = 'Loading file…';

export const FILE_LOAD_FAILED_MESSAGE = 'Could not load the file.';

export const PDF_VIEWER_DOC_ID = 'form-template-version';

export const FILE_MODAL_ID = 'form-change-history-file-modal';

export const PDF_FILE_EXTENSION = 'pdf';

export const PDF_DOWNLOAD_FILE_NAME = {
  [TemplateSide.BEFORE]: 'form-template-previous',
  [TemplateSide.AFTER]: 'form-template-this-version',
};

/** Every request a sandboxed HTML version may not make: no scripts, forms, popups or same-origin. */
export const HTML_FRAME_SANDBOX = '';

export const TemplateRequestStatus = {
  EMPTY: 'empty',
  OTHER_KIND: 'other-kind',
  LOADING: 'loading',
  LOADED: 'loaded',
  UNAVAILABLE: 'unavailable',
  NO_SNAPSHOT: 'no-snapshot',
  NO_DOCUMENT: 'no-document',
  NOT_RENDERED: 'not-rendered',
  FAILED: 'failed',
};

export const FormHistoryChip = {
  OVERRIDE: 'override',
  GLOBAL: 'global',
  NOT_EFFECTIVE: 'not-effective',
};

export const YOUR_OVERRIDE_LABEL = 'Your override';

export const NOT_EFFECTIVE_LABEL = 'Not effective for you';

export const IN_EFFECT_NOTE = 'In effect for your form';

export const OVERRIDE_LIFECYCLE_NOTE = {
  [AuditOperation.CREATED]:
    "You started overriding this form's template; global template changes stop reaching your form.",
  [AuditOperation.DELETED]: 'Override removed; your form follows the Global template again.',
};

export const NOT_EFFECTIVE_NOTE = 'Your override replaced it, so this never reached your form.';

export const LOCAL_TIME_OPTIONS = { includeLocalOffset: true };

/**
 * A failed read's status by HTTP code; any other code fails with a retry. 404 (no such change for
 * the reader) and 410 (its stored values are gone) are not retryable.
 */
export const TEMPLATE_ERROR_STATUS = {
  [HTTP_RESPONSE_STATUS_CODES.NOT_FOUND]: TemplateRequestStatus.UNAVAILABLE,
  [HTTP_RESPONSE_STATUS_CODES.GONE]: TemplateRequestStatus.UNAVAILABLE,
};

/** 409: a change written without a stored image (a bulk or explicit capture) has none to compare. */
export const COMPARE_ERROR_STATUS = {
  ...TEMPLATE_ERROR_STATUS,
  [HTTP_RESPONSE_STATUS_CODES.CONFLICT]: TemplateRequestStatus.NO_SNAPSHOT,
};

/** 409: that side holds no Prism document; 422: the renderer refused it, worth another try. */
export const PREVIEW_ERROR_STATUS = {
  ...TEMPLATE_ERROR_STATUS,
  [HTTP_RESPONSE_STATUS_CODES.CONFLICT]: TemplateRequestStatus.NO_DOCUMENT,
  [HTTP_RESPONSE_STATUS_CODES.UNPROCESSABLE_ENTITY]: TemplateRequestStatus.NOT_RENDERED,
};

export const COMPARE_WITH_CURRENT_LABEL = 'Compare with current';

export const COMPARE_MODAL_ID = 'form-change-history-compare-modal';

export const VERSION_PICKER_NAME = 'formHistoryVersion';

export const VERSION_PICKER_LABEL = 'Version';

export const VERSION_PICKER_WIDTH = '420px';

export const VERSION_OPTION_KEY = 'auditId';

export const BACK_TO_CHANGE_LABEL = 'Back to the change';

const VERSION_UNAVAILABLE_MESSAGE = 'This version is no longer available.';

export const TEMPLATE_DIFFERENCES_TITLE = 'Template differences';

export const COMPARE_COLUMN_LABELS = { field: 'Field', was: 'This version', became: 'Current' };

export const COMPARE_HTML_TITLE = {
  [HtmlChange.ADDED]: 'This version has no HTML template',
  [HtmlChange.REMOVED]: 'The current form has no HTML template',
  [HtmlChange.REPLACED]: 'The HTML template differs',
  [HtmlChange.UNCHANGED]: 'The HTML template is the same.',
};

/** The compare's message per read status; a loaded compare has one only when nothing differs. */
export const COMPARE_STATE_MESSAGE = {
  [TemplateRequestStatus.LOADING]: {
    message: 'Comparing with the current form…',
    isLoading: true,
    hasRetry: false,
  },
  [TemplateRequestStatus.LOADED]: {
    message: 'No differences from the current form.',
    isLoading: false,
    hasRetry: false,
  },
  [TemplateRequestStatus.UNAVAILABLE]: {
    message: VERSION_UNAVAILABLE_MESSAGE,
    isLoading: false,
    hasRetry: false,
  },
  [TemplateRequestStatus.NO_SNAPSHOT]: {
    message: 'This version has no snapshot to compare.',
    isLoading: false,
    hasRetry: false,
  },
  [TemplateRequestStatus.FAILED]: {
    message: 'Could not compare this version with the current form.',
    isLoading: false,
    hasRetry: true,
  },
};

export const PREVIEW_MODAL_ID = 'form-change-history-preview-modal';

export const PreviewKind = {
  CHANGE: 'change',
  VERSION: 'version',
};

export const PREVIEW_ACTION_LABEL = {
  [PreviewKind.CHANGE]: 'Preview before → after',
  [PreviewKind.VERSION]: 'Preview this version → current',
};

export const PREVIEW_MODAL_TITLE = {
  [PreviewKind.CHANGE]: 'Preview · before → after',
  [PreviewKind.VERSION]: 'Preview · this version → current',
};

export const PREVIEW_PANE_TITLE = {
  [TemplateSide.BEFORE]: 'Before',
  [TemplateSide.AFTER]: 'After',
  [TemplateSide.CURRENT]: 'Current',
};

export const VERSION_PANE_TITLE = 'This version';

export const SAMPLE_DATA_NOTICE =
  'Rendered with sample data, not a real loan — values on the page are placeholders.';

/** A preview's panes by position: the older side first (before, or the listed version). */
const PreviewPane = {
  OLDER: 0,
  NEWER: 1,
};

export const PREVIEW_PANES = [PreviewPane.OLDER, PreviewPane.NEWER];

/** The panes a part is marked in: an added part exists only in the newer side, a removed one only in the older. */
export const MARK_PANES_BY_ROW_KIND = {
  [PartRowKind.ADDED]: [PreviewPane.NEWER],
  [PartRowKind.REMOVED]: [PreviewPane.OLDER],
};

export const PART_GROUP_SINGULAR = {
  [TemplatePartGroup.DYNAMIC_FIELD]: 'Dynamic field',
  [TemplatePartGroup.SIGNATURE]: 'Signature',
  [TemplatePartGroup.TEXT]: 'Text',
  [TemplatePartGroup.IMAGE]: 'Image',
  [TemplatePartGroup.LAYOUT]: 'Layout',
};

export const FIRST_PAGE = 1;

export const PAGES_LABEL = 'Pages';

export const NO_MARKS_ON_PAGE_MESSAGE = 'No marked changes on this page.';

export const MARK_SELECT_HINT = 'Select a change to highlight it on the pages.';

export const ELSEWHERE_LABEL = 'Elsewhere';

export const UNPLACED_MARKS_LABEL = 'Not placed on a page';

export const MARKS_UNAVAILABLE_NOTE = "Changes couldn't be placed on the pages.";

export const MARKS_APPROXIMATE_NOTE =
  'Marks come from the template layout; sample values can shift them slightly.';

/** A preview pane's message per read status, until its pages have rendered. */
export const PREVIEW_STATE_MESSAGE = {
  [TemplateRequestStatus.LOADING]: {
    message: 'Loading the preview…',
    isLoading: true,
    hasRetry: false,
  },
  [TemplateRequestStatus.NO_DOCUMENT]: {
    message: 'No template on this side.',
    isLoading: false,
    hasRetry: false,
  },
  [TemplateRequestStatus.UNAVAILABLE]: {
    message: VERSION_UNAVAILABLE_MESSAGE,
    isLoading: false,
    hasRetry: false,
  },
  [TemplateRequestStatus.NOT_RENDERED]: {
    message: "Preview didn't render.",
    isLoading: false,
    hasRetry: true,
  },
  [TemplateRequestStatus.FAILED]: {
    message: 'Could not load the preview.',
    isLoading: false,
    hasRetry: true,
  },
};

export const CLOSED_COMPARE = { auditId: null, isOpen: false, rows: [], openRow: null };
